// The Work Plan question catalog (SQ-486), served by GET api/v1/work-plan/catalog
// from backend/Accusite/Api/Catalog/work-plan-catalog.json: the one copy of the
// questions a work item's operations bring in, the additionalDetails key each
// answer is stored under and the answers offered. Android's WorkPlan.kt is
// checked against the same file.

export type WorkPlanQuestionType = 'choice' | 'integer' | 'text' | 'materialSpec';

export interface WorkPlanQuestionDto {
  key: string;
  title: string;
  type: WorkPlanQuestionType;
  options?: string[];
  /** The answers offered on a Misc. item in place of options. */
  miscOptions?: string[];
  /** The answers offered on a stump work item in place of options (SQ-644: the loader's hauling). */
  stumpOptions?: string[];
  /** On a stump work item, the answers narrowed by another question's answer ({cleanup: {answer: options}}). */
  stumpOptionsByAnswer?: Record<string, Record<string, string[]>>;
  /** On a stump work item, an answer's label under another question's answer ({cleanup: {answer: {option: label}}}). */
  stumpLabelsByAnswer?: Record<string, Record<string, Record<string, string>>>;
  /** Asked only while another question holds one of these answers. */
  followsAnswers?: Record<string, string[]>;
  /** A title that depends on the answer it follows. */
  titleByAnswer?: Record<string, string>;
}

export interface WorkPlanSectionDto {
  id: string;
  title: string;
  miscTitle?: string;
  /** Operation types that bring the section in (any one of them). */
  operations?: string[];
  /** A section brought in by a piece of equipment on the work item instead. */
  equipment?: {category: string; type: string};
  questions: WorkPlanQuestionDto[];
}

export interface MaterialSpecDto {
  diameter: number;
  lengthMode: string;
  lengthFt: number;
  lengthIn: number;
  location: string;
  locationNote: string;
}

export interface WorkPlanCatalogDto {
  version: number;
  keys: string[];
  sections: WorkPlanSectionDto[];
  materialSpec: {fields: string[]; lengthMode: string[]; location: string[]};
  rules: {
    clearedByAnswer?: Record<string, Record<string, string[]>>;
    webOnlyOptions?: Record<string, string[]>;
  };
}

export const EMPTY_MATERIAL_SPEC: MaterialSpecDto = {diameter: 0, lengthMode: '', lengthFt: 0, lengthIn: 0, location: '', locationNote: ''};

/** The sections a work item gets: the union over its operations, plus the loader section when a loader is on it. */
export function workPlanSectionsFor(catalog: WorkPlanCatalogDto, operationTypes: string[], hasLoader: boolean): WorkPlanSectionDto[] {
  return catalog.sections.filter(section =>
    section.equipment ? hasLoader : (section.operations ?? []).some(op => operationTypes.includes(op)));
}

/** Whether a follow-up question is asked, given the answers so far. */
export function workPlanQuestionApplies(question: WorkPlanQuestionDto, answers: Record<string, unknown>): boolean {
  if (!question.followsAnswers) return true;
  return Object.entries(question.followsAnswers).every(([key, accepted]) => accepted.includes(String(answers[key] ?? '')));
}

/** A stump answer set narrowed by the answers so far (the loader's hauling by Cleanup), else the full stump set. */
function stumpOptionsFor(question: WorkPlanQuestionDto, answers: Record<string, unknown>): string[] | undefined {
  for (const [key, byAnswer] of Object.entries(question.stumpOptionsByAnswer ?? {})) {
    const narrowed = byAnswer[String(answers[key] ?? '')];
    if (narrowed) return narrowed;
  }
  return question.stumpOptions;
}

/**
 * The answers a choice question offers here: the catalog's, the Misc. or stump set on such an item (narrowed by the
 * answers so far), plus the web's own explicit answers.
 */
export function workPlanOptions(catalog: WorkPlanCatalogDto, question: WorkPlanQuestionDto, isMisc: boolean, isStump = false,
                                answers: Record<string, unknown> = {}): string[] {
  const stump = isStump ? stumpOptionsFor(question, answers) : undefined;
  const base = isMisc && question.miscOptions ? question.miscOptions
    : stump ?? question.options ?? [];
  const extra = catalog.rules.webOnlyOptions?.[question.key] ?? [];
  return [...extra.filter(option => !base.includes(option)), ...base];
}

/** An answer's label here: on a stump item it can depend on another answer (Stump Wood reads Wood & Grindings). */
export function workPlanOptionLabel(question: WorkPlanQuestionDto, option: string, isStump: boolean, answers: Record<string, unknown>): string {
  if (!isStump) return option;
  for (const [key, byAnswer] of Object.entries(question.stumpLabelsByAnswer ?? {})) {
    const label = byAnswer[String(answers[key] ?? '')]?.[option];
    if (label) return label;
  }
  return option;
}

/** Keys an answer clears (Fell Whole Tree takes the climbing questions with it). */
export function workPlanClearedBy(catalog: WorkPlanCatalogDto, key: string, answer: string | undefined): string[] {
  return answer === undefined ? [] : catalog.rules.clearedByAnswer?.[key]?.[answer] ?? [];
}
